import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, retry, throwError, timer } from 'rxjs';

import { ApiService } from '@features/api';
import { AuthService } from '@features/auth';

import { Basket, ValidateCheckoutResponse } from '../models';
import { Booking } from '@features/bookings';

@Injectable({ providedIn: 'root' })
export class BasketService {
  #api = inject(ApiService);
  #auth = inject(AuthService);

  addTickets(tickets: { quantity: number; ticket_option: string; ticket_type_id: string }[]): Observable<Basket> {
    return this.#api.post<{ basket: Basket }>('/basket', { ticket_types: tickets }).pipe(map(({ basket }) => basket));
  }

  updateBasket(tickets: { quantity: number; ticket_option: string; ticket_type_id: string }[]): Observable<Basket> {
    return this.#api.patch<{ basket: Basket }>('/basket', { ticket_types: tickets }).pipe(map(({ basket }) => basket));
  }

  getBasket(): Observable<Basket | null> {
    return this.#api.get<{ basket: Basket | null }>('/basket').pipe(map(({ basket }) => basket));
  }

  validateCheckout() {
    return this.#api.get<ValidateCheckoutResponse>('/basket/checkout/validate');
  }

  getPaymentIntent(): Observable<{ clientSecret: string }> {
    // Only fetch payment intent for authenticated users
    if (!this.#auth.authenticated()) {
      return of({ clientSecret: '' });
    }
    return this.#api.get<{ clientSecret: string }>('/basket/checkout').pipe(
      // 409 (a concurrent checkout for this basket is in flight) and 503 (the existing payment
      // session could not be verified) are transient and retryable per the API. Back off briefly
      // and retry; rethrow anything else immediately.
      retry({
        count: 3,
        delay: (error, attempt) =>
          error?.status === 409 || error?.status === 503 ? timer(attempt * 1000) : throwError(() => error),
      }),
    );
  }

  checkoutWithCredit() {
    return this.#api.get<{ order: Booking }>('/basket/checkout/credit');
  }

  cancel() {
    return this.#api.delete('/basket');
  }

  /**
   * Expire the current ACTIVE basket on the backend when the customer dismisses the "out of time"
   * modal, rather than waiting up to a minute for the lifecycle cron. The backend either clears the
   * basket (still selecting / no payment in flight) or moves it to STALE (checked out, payment
   * pending) so a delayed Ryft webhook can still reconcile.
   *
   * Guests have no authenticated basket to expire, so this is a no-op for them. Any backend error
   * (network failure, basket already staled) is swallowed: the caller should always be free to
   * navigate the customer away regardless of the outcome.
   */
  expireBasket(): Observable<void> {
    if (!this.#auth.authenticated()) {
      return of(undefined);
    }

    return this.#api.post('/basket/expire', {}).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }
}
